import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Copy, Download, GripVertical, Loader2, Plus, Search, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const SOURCES = [
  { key: 'handpick', label: 'Hand-pick' },
  { key: 'section', label: 'Featured Section' },
  { key: 'deck', label: 'Deck' },
];

const PLATFORMS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'tiktok', label: 'TikTok' },
  { key: 'x', label: 'X' },
];

const TONES = [
  { key: 'heartfelt', label: 'Heartfelt' },
  { key: 'playful', label: 'Playful' },
  { key: 'minimal', label: 'Minimal' },
];

const MAX_PRODUCTS = 10;

export default function CarouselBuilder({ theme, ThemePicker }) {
  const [products, setProducts] = useState([]);
  const [sections, setSections] = useState([]);
  const [decks, setDecks] = useState([]);
  const [source, setSource] = useState('handpick');
  const [sourceId, setSourceId] = useState('');
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState([]); // product objects, ordered
  const [coverTitle, setCoverTitle] = useState('Gift Ideas');
  const [coverSubtitle, setCoverSubtitle] = useState('A Mementa edit');
  const [size, setSize] = useState('square');
  const [previewKey, setPreviewKey] = useState('cover'); // 'cover' | product id | 'cta'
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [tone, setTone] = useState('heartfelt');
  const [caption, setCaption] = useState('');
  const [captionLoading, setCaptionLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const cacheRef = useRef({});

  useEffect(() => {
    Promise.all([api.get('/products'), api.get('/featured-sections'), api.get('/decks')])
      .then(([p, s, d]) => { setProducts(p.data); setSections(s.data); setDecks(d.data); })
      .catch(() => toast.error('Failed to load carousel sources'));
  }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const resolveIds = (ids) =>
    ids.map((id) => productMap[id]).filter(Boolean).slice(0, MAX_PRODUCTS);

  const pickSection = (id) => {
    setSourceId(id);
    const sec = sections.find((s) => s.id === id);
    if (!sec) return;
    const ids = [];
    (sec.item_ids || []).forEach((itemId, i) => {
      const type = (sec.item_types || [])[i] || 'product';
      if (type === 'product') ids.push(itemId);
      else if (type === 'deck') {
        const deck = decks.find((d) => d.id === itemId);
        if (deck) ids.push(...(deck.product_ids || []));
      }
    });
    const resolved = resolveIds([...new Set(ids)]);
    setPicked(resolved);
    if (!resolved.length) toast.warning('This section references products that are no longer in the catalog — hand-pick products instead, or update the section.');
    setCoverTitle(sec.name || 'Gift Ideas');
    setCoverSubtitle(sec.description || 'A Mementa edit');
  };

  const pickDeck = (id) => {
    setSourceId(id);
    const deck = decks.find((d) => d.id === id);
    if (!deck) return;
    const resolved = resolveIds(deck.product_ids || []);
    setPicked(resolved);
    if (!resolved.length) toast.warning('This deck references products that are no longer in the catalog — hand-pick products instead, or update the deck.');
    setCoverTitle(deck.title || 'Gift Ideas');
    setCoverSubtitle(deck.description || 'A Mementa edit');
  };

  const addProduct = (p) => {
    if (picked.find((x) => x.id === p.id)) return;
    if (picked.length >= MAX_PRODUCTS) return toast.error(`Maximum ${MAX_PRODUCTS} products (Meta's carousel limit)`);
    setPicked([...picked, p]);
  };

  const removeProduct = (id) => setPicked(picked.filter((p) => p.id !== id));

  const moveProduct = (idx, dir) => {
    const next = [...picked];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setPicked(next);
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return products.slice(0, 20);
    return products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 20);
  }, [products, search]);

  const fetchCard = async (key) => {
    if (key === 'cover' || key === 'cta') {
      const res = await api.post('/admin/ads/render-card', {
        kind: key,
        title: key === 'cover' ? coverTitle : '',
        subtitle: key === 'cover' ? coverSubtitle : '',
        size, theme,
      }, { responseType: 'blob' });
      return res;
    }
    return api.get('/admin/ads/render', {
      params: { product_id: key, size, theme },
      responseType: 'blob',
    });
  };

  // preview render (cached per key+size+theme+cover text)
  useEffect(() => {
    const cacheId = `${previewKey}|${size}|${theme}|${previewKey === 'cover' ? coverTitle + coverSubtitle : ''}`;
    if (cacheRef.current[cacheId]) { setPreviewUrl(cacheRef.current[cacheId]); return; }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      fetchCard(previewKey)
        .then((res) => {
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          cacheRef.current[cacheId] = url;
          setPreviewUrl(url);
        })
        .catch(() => !cancelled && toast.error('Preview failed'))
        .finally(() => !cancelled && setPreviewLoading(false));
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [previewKey, size, theme, coverTitle, coverSubtitle]);

  const cards = useMemo(() => [
    { key: 'cover', label: 'Cover' },
    ...picked.map((p, i) => ({ key: p.id, label: `${i + 1}. ${p.name.slice(0, 22)}` })),
    { key: 'cta', label: 'CTA card' },
  ], [picked]);

  const downloadAll = async () => {
    if (picked.length < 2) return toast.error('Add at least 2 products for a carousel');
    setDownloading(true);
    try {
      const slugBase = coverTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30) || 'carousel';
      const queue = [
        { key: 'cover', name: `mementa-${slugBase}-01-cover.png` },
        ...picked.map((p, i) => ({
          key: p.id,
          name: `mementa-${slugBase}-${String(i + 2).padStart(2, '0')}-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}.png`,
        })),
        { key: 'cta', name: `mementa-${slugBase}-${String(picked.length + 2).padStart(2, '0')}-cta.png` },
      ];
      for (let i = 0; i < queue.length; i += 1) {
        setProgress(`Rendering card ${i + 1} of ${queue.length}…`);
        const res = await fetchCard(queue[i].key);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = queue[i].name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }
      toast.success(`${queue.length} carousel cards downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
      setProgress('');
    }
  };

  const generateCaption = async () => {
    if (picked.length < 2) return toast.error('Add at least 2 products first');
    setCaptionLoading(true);
    try {
      const res = await api.post('/admin/ads/carousel-caption', {
        title: coverTitle,
        subtitle: coverSubtitle,
        product_ids: picked.map((p) => p.id),
        platform,
        tone,
      });
      setCaption(res.data.caption);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Caption generation failed');
    } finally {
      setCaptionLoading(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Caption copied');
    } catch {
      toast.error('Copy failed — select the text manually');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="carousel-builder">
      {/* Left: contents */}
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9E6752' }}>Source</p>
          <div className="flex gap-1.5 mb-3">
            {SOURCES.map((s) => (
              <button
                key={s.key}
                onClick={() => {
                  setSource(s.key);
                  setSourceId('');
                  if (s.key !== source) { setPicked([]); setCoverTitle('Gift Ideas'); setCoverSubtitle('A Mementa edit'); }
                }}
                data-testid={`carousel-source-${s.key}`}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                style={{
                  background: source === s.key ? '#534145' : '#FFF8F0',
                  color: source === s.key ? '#FFFAF5' : '#534145',
                  border: '1px solid #53414533',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
          {source === 'section' && (
            <select
              value={sourceId}
              onChange={(e) => pickSection(e.target.value)}
              data-testid="carousel-section-select"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: '#E5E0DA', color: '#20212B' }}
            >
              <option value="">Choose a featured section…</option>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {source === 'deck' && (
            <select
              value={sourceId}
              onChange={(e) => pickDeck(e.target.value)}
              data-testid="carousel-deck-select"
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ borderColor: '#E5E0DA', color: '#20212B' }}
            >
              <option value="">Choose a deck…</option>
              {decks.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          )}
          {source === 'handpick' && (
            <>
              <div className="relative mb-2">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9E6752' }} />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products to add…" className="pl-8" data-testid="carousel-search-input" />
              </div>
              <div className="max-h-44 overflow-y-auto divide-y" style={{ borderColor: '#F3F4F6' }}>
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => addProduct(p)}
                    data-testid={`carousel-add-${p.id}`}
                    className="w-full flex items-center gap-2 px-1.5 py-1.5 text-left hover:bg-[#FFF8F0] rounded"
                  >
                    <Plus size={13} style={{ color: '#9E6752' }} />
                    <span className="text-xs truncate flex-1" style={{ color: '#20212B' }}>{p.name}</span>
                    <span className="text-[11px]" style={{ color: '#9E6752' }}>${p.price}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Picked list */}
        <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9E6752' }}>
            Carousel cards ({picked.length}/{MAX_PRODUCTS} products)
          </p>
          {picked.length === 0 ? (
            <p className="text-xs py-3" style={{ color: '#9E6752' }}>No products yet — pick a source above.</p>
          ) : (
            <div className="space-y-1.5" data-testid="carousel-picked-list">
              {picked.map((p, i) => {
                const thumb = p.images?.[0] || p.image_url;
                return (
                  <div key={p.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5" style={{ background: '#FFF8F0' }}>
                    <div className="flex flex-col">
                      <button onClick={() => moveProduct(i, -1)} className="leading-none text-[10px]" style={{ color: '#9E6752' }} aria-label="Move up">▲</button>
                      <button onClick={() => moveProduct(i, 1)} className="leading-none text-[10px]" style={{ color: '#9E6752' }} aria-label="Move down">▼</button>
                    </div>
                    {thumb && <img src={thumb} alt="" className="w-8 h-8 rounded object-cover" loading="lazy" />}
                    <span className="text-xs truncate flex-1" style={{ color: '#20212B' }}>{i + 1}. {p.name}</span>
                    <button onClick={() => removeProduct(p.id)} data-testid={`carousel-remove-${p.id}`}>
                      <X size={13} style={{ color: '#8C4A4F' }} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cover text */}
        <div className="rounded-2xl bg-white border p-4 space-y-3" style={{ borderColor: '#E5E0DA' }}>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#9E6752' }}>Cover card</p>
          <div>
            <label className="text-xs font-semibold" style={{ color: '#534145' }}>Title</label>
            <Input value={coverTitle} onChange={(e) => setCoverTitle(e.target.value)} data-testid="carousel-cover-title" />
          </div>
          <div>
            <label className="text-xs font-semibold" style={{ color: '#534145' }}>Subtitle</label>
            <Input value={coverSubtitle} onChange={(e) => setCoverSubtitle(e.target.value)} data-testid="carousel-cover-subtitle" />
          </div>
        </div>
      </div>

      {/* Right: preview + export */}
      <div className="lg:col-span-3 space-y-4">
        <div className="rounded-2xl bg-white border p-4 flex flex-wrap items-center gap-4" style={{ borderColor: '#E5E0DA' }}>
          {ThemePicker}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#9E6752' }}>Size</span>
            {['square', 'portrait'].map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                data-testid={`carousel-size-${s}`}
                className="px-2.5 py-1.5 rounded-full text-xs font-semibold capitalize transition-all"
                style={{
                  background: size === s ? '#9E6752' : '#FFF8F0',
                  color: size === s ? '#FFFAF5' : '#534145',
                  border: '1px solid #9E675233',
                }}
              >
                {s === 'square' ? 'Square 1080×1080' : 'Portrait 1080×1350'}
              </button>
            ))}
          </div>
        </div>

        {/* Card strip */}
        <div className="rounded-2xl bg-white border p-3 flex gap-1.5 overflow-x-auto" style={{ borderColor: '#E5E0DA' }} data-testid="carousel-card-strip">
          {cards.map((c) => (
            <button
              key={c.key}
              onClick={() => setPreviewKey(c.key)}
              data-testid={`carousel-card-chip-${c.key}`}
              className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap transition-all flex items-center gap-1"
              style={{
                background: previewKey === c.key ? '#534145' : '#FFF8F0',
                color: previewKey === c.key ? '#FFFAF5' : '#534145',
                border: '1px solid #53414522',
              }}
            >
              <GripVertical size={10} className="opacity-40" />
              {c.label}
            </button>
          ))}
        </div>

        <div className="rounded-2xl bg-white border p-4 flex items-center justify-center" style={{ borderColor: '#E5E0DA', minHeight: 320 }} data-testid="carousel-preview-panel">
          {previewLoading ? (
            <div className="flex flex-col items-center gap-2 py-16">
              <Loader2 size={26} className="animate-spin" style={{ color: '#9E6752' }} />
              <p className="text-xs" style={{ color: '#9E6752' }}>Rendering card…</p>
            </div>
          ) : previewUrl ? (
            <img src={previewUrl} alt="Carousel card preview" data-testid="carousel-preview-image" className="rounded-xl shadow-md" style={{ maxHeight: 440, maxWidth: '100%', objectFit: 'contain' }} />
          ) : null}
        </div>

        <div className="rounded-2xl bg-white border p-4 flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: '#E5E0DA' }}>
          <p className="text-xs" style={{ color: '#9E6752' }}>
            Exports {picked.length + 2} numbered cards (cover + {picked.length} products + CTA) — upload them in order in Ads Manager.
          </p>
          <Button
            onClick={downloadAll}
            disabled={downloading || picked.length < 2}
            data-testid="carousel-download-btn"
            className="text-white"
            style={{ background: '#534145' }}
          >
            {downloading ? (
              <><Loader2 size={15} className="animate-spin mr-2" /> {progress}</>
            ) : (
              <><Download size={15} className="mr-2" /> Download {picked.length + 2} cards ({theme})</>
            )}
          </Button>
        </div>

        {/* AI caption for the whole carousel */}
        <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }} data-testid="carousel-caption-card">
          <p className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5" style={{ color: '#9E6752' }}>
            <Sparkles size={13} /> AI Caption — one post for the whole carousel
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <div className="flex items-center gap-1.5">
              {PLATFORMS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPlatform(p.key)}
                  data-testid={`carousel-caption-platform-${p.key}`}
                  className="px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all"
                  style={{
                    background: platform === p.key ? '#534145' : '#FFF8F0',
                    color: platform === p.key ? '#FFFAF5' : '#534145',
                    border: '1px solid #53414533',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {TONES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTone(t.key)}
                  data-testid={`carousel-caption-tone-${t.key}`}
                  className="px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all"
                  style={{
                    background: tone === t.key ? '#9E6752' : '#FFF8F0',
                    color: tone === t.key ? '#FFFAF5' : '#534145',
                    border: '1px solid #9E675233',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <Button
              size="sm"
              onClick={generateCaption}
              disabled={captionLoading || picked.length < 2}
              data-testid="carousel-caption-generate-btn"
              className="text-white"
              style={{ background: '#9E6752' }}
            >
              {captionLoading ? (
                <><Loader2 size={14} className="animate-spin mr-1.5" /> Writing…</>
              ) : (
                <><Sparkles size={14} className="mr-1.5" /> {caption ? 'Regenerate' : 'Generate caption'}</>
              )}
            </Button>
          </div>
          {caption && (
            <div className="space-y-2">
              <Textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={Math.min(10, Math.max(4, caption.split('\n').length + 1))}
                className="text-sm"
                data-testid="carousel-caption-text"
              />
              <Button size="sm" variant="outline" onClick={copyCaption} data-testid="carousel-caption-copy-btn">
                {copied ? <><Check size={14} className="mr-1.5" style={{ color: '#4B6043' }} /> Copied</> : <><Copy size={14} className="mr-1.5" /> Copy caption</>}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
